'use client';
import { JSX, useEffect } from 'react';
import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';
import { sendGTMFormErrorEvent } from '@/components/helpers/analytics/gtm';
import { IAttextFieldProps } from './at-text-field.types';

/**
 * `AtTextField` is a styled text input component integrated with `react-hook-form`.
 * It handles error display, accessibility, required field logic, and GTM error tracking.
 *
 * Should be used within a `FormProvider` context (e.g. `MlForm`).
 *
 */
export const AtTextField = ({
  id,
  label,
  className,
  required,
  ...props
}: IAttextFieldProps): JSX.Element => {
  const {
    register,
    formState: { errors, isSubmitting },
    getValues,
  } = useFormContext();

  const hasError = Boolean(errors[id]);
  const errorMessage = hasError ? (errors[id]?.message as string) : '';

  useEffect(() => {
    if (hasError) {
      sendGTMFormErrorEvent({
        form_name: getValues('formName'),
        form_field: id,
        error_message: errorMessage,
      });
    }
  }, [errorMessage, getValues, hasError, id]);

  return (
    <div className={classNames('relative flex w-full flex-col')}>
      {label && (
        <label
          htmlFor={id}
          className={classNames(
            'absolute -top-3 left-4 bg-skin-primary px-1 text-10 text-page-link md:-top-[0.75rem] md:text-sm',
            hasError && 'text-skin-error',
            {
              'after:ml-0.5 after:text-skin-alert after:content-["*"]':
                required,
            },
          )}
        >
          {label}
        </label>
      )}
      <input
        {...props}
        {...register(id, {
          required,
          disabled: props.disabled || isSubmitting,
        })}
        id={id}
        aria-required={required}
        className={classNames(
          'h-10 w-full rounded-15 border-2 bg-transparent px-4 focus:outline-none md:h-[3.938rem]',
          className,
          {
            'border-skin-error focus:outline-red-400': hasError,
            'border-skin-stroke-secondary': !hasError,
          },
        )}
        {...(hasError
          ? {
              'aria-invalid': hasError,
              'aria-describedby': `errorMessage-${id}`,
            }
          : {})}
      />
      {hasError && (
        <p
          className="mt-2 pl-4 text-xs text-skin-alert"
          id={`errorMessage-${id}`}
          role="alert"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};
